import type { CircuitState, GateType } from '../circuit/SignalState';
import { GATES } from '../data/gateDefinitions';
import { gateSymbol } from './GateSymbol';
import type { QualityMode } from '../scene/PerformanceManager';
import type { ComponentMeta } from '../components/BaseComponent';

export interface UIActions {
  gate: (type: GateType) => void;
  toggle: (key: 'K1' | 'K2') => void;
  resetView: () => void;
  autoDemo: () => void;
  pauseDemo: () => void;
  resetDemo: () => void;
  practice: () => void;
  exitPractice: () => void;
  wiringTerminal: (id: string) => void;
}

export class HUD {
  private truthBody!: HTMLElement;
  private stateValues!: HTMLElement;
  private ledBadge!: HTMLElement;
  private gateTitle!: HTMLElement;
  private chipBadge!: HTMLElement;
  private symbol!: HTMLElement;
  private explanation!: HTMLElement;
  private infoCard!: HTMLElement;
  private toast!: HTMLElement;
  private demoText!: HTMLElement;
  private practicePanel!: HTMLElement;
  private practiceStep!: HTMLElement;
  private wiringTerminals!: HTMLElement;

  constructor(private root: HTMLElement, quality: QualityMode, private actions: UIActions) {
    this.root.innerHTML = `
      <main class="lab-shell">
        <section class="viewport" id="viewport">
          <header class="topbar glass">
            <div><div class="eyebrow">通用技术 · 数字电路</div><h1>数字逻辑门 3D 互动实验</h1></div>
            <div class="power-strip">
              <span class="status-dot"></span><span>3.7V Li-Po</span><span>VCC 正常</span><span>GND 0V</span>
              <span class="quality">${quality}</span>
            </div>
          </header>
          <div class="gate-selector glass">
            <span>实验模式</span>
            ${(['AND','NAND','OR'] as GateType[]).map((type) => `<button data-gate="${type}">${GATES[type].zh}<small>${type}</small></button>`).join('')}
          </div>
          <div class="canvas-help glass">左键旋转 · 滚轮缩放 · 右键平移<br>点击元件查看 / 点击 K1、K2 拨动</div>
          <div class="toolbar glass">
            <button data-action="reset-view">⌂ 重置视角</button>
            <button data-action="practice">⌁ 接线练习</button>
            <button data-action="auto">▶ 自动演示</button>
            <button data-action="pause">Ⅱ 暂停/继续</button>
            <button data-action="reset-demo">↺ 重置演示</button>
          </div>
          <div class="switch-controls">
            <button class="switch-control" data-switch="K1"><b>K1</b><span>输入 A</span><em></em></button>
            <button class="switch-control" data-switch="K2"><b>K2</b><span>输入 B</span><em></em></button>
          </div>
          <div class="wiring-terminals" aria-hidden="true"></div>
          <div class="hover-label"></div>
          <div class="toast"></div>
        </section>
        <aside class="side-panel">
          <section class="logic-card glass">
            <div class="logic-heading"><div><div class="eyebrow">CURRENT GATE</div><h2></h2></div><span class="chip-badge"></span></div>
            <button class="gate-symbol" title="点击查看当前逻辑门说明"></button>
            <div class="expression"></div>
          </section>
          <section class="state-card glass">
            <div class="state-values"></div>
            <div class="led-badge"></div>
            <div class="connection-note">LED 接法：<b>低电平点亮</b><br><code>VCC → LED → R3 → Y</code></div>
          </section>
          <section class="truth-card glass"><h3>真值表 <small>Truth Table</small></h3><table><thead><tr><th>A</th><th>B</th><th>Y</th><th>LED</th></tr></thead><tbody></tbody></table></section>
          <section class="misconception glass">
            <h3>关键提醒</h3>
            <p>开关断开 ≠ 逻辑 0：上拉使输入为 <b>1</b>。</p>
            <p>LED 亮 ≠ 输出 1：本电路 <b>Y=0 时亮</b>。</p>
          </section>
        </aside>
        <section class="info-card glass" aria-hidden="true">
          <button class="close" aria-label="关闭">×</button><div class="eyebrow">COMPONENT</div><h2></h2><h4></h4>
          <dl></dl>
        </section>
        <section class="practice-panel glass" aria-hidden="true">
          <div class="practice-head"><div><div class="eyebrow">WIRING PRACTICE</div><h2>接线练习</h2></div><button data-action="exit-practice">×</button></div>
          <div class="progress"><i></i></div><p class="practice-step"></p>
          <p class="practice-hint">依次点击场景中的起点与终点。正确导线会自动吸附。</p>
          <button data-action="exit-practice">退出练习</button>
        </section>
        <div class="demo-caption glass"></div>
      </main>`;
    this.truthBody = this.root.querySelector('tbody')!;
    this.stateValues = this.root.querySelector('.state-values')!;
    this.ledBadge = this.root.querySelector('.led-badge')!;
    this.gateTitle = this.root.querySelector('.logic-heading h2')!;
    this.chipBadge = this.root.querySelector('.chip-badge')!;
    this.symbol = this.root.querySelector('.gate-symbol')!;
    this.explanation = this.root.querySelector('.expression')!;
    this.infoCard = this.root.querySelector('.info-card')!;
    this.toast = this.root.querySelector('.toast')!;
    this.demoText = this.root.querySelector('.demo-caption')!;
    this.practicePanel = this.root.querySelector('.practice-panel')!;
    this.practiceStep = this.root.querySelector('.practice-step')!;
    this.wiringTerminals = this.root.querySelector('.wiring-terminals')!;
    this.bind();
  }

  private bind(): void {
    this.root.querySelectorAll<HTMLElement>('[data-gate]').forEach((button) => button.addEventListener('click', () => this.actions.gate(button.dataset.gate as GateType)));
    this.root.querySelectorAll<HTMLElement>('[data-switch]').forEach((button) => button.addEventListener('click', () => this.actions.toggle(button.dataset.switch as 'K1'|'K2')));
    const actionMap: Record<string, () => void> = {
      'reset-view': this.actions.resetView, auto: this.actions.autoDemo, pause: this.actions.pauseDemo,
      'reset-demo': this.actions.resetDemo, practice: this.actions.practice, 'exit-practice': this.actions.exitPractice
    };
    this.root.querySelectorAll<HTMLElement>('[data-action]').forEach((button) => button.addEventListener('click', () => actionMap[button.dataset.action!]?.()));
    this.root.querySelector('.info-card .close')?.addEventListener('click', () => this.hideInfo());
    this.symbol.addEventListener('click', () => this.showGateInfo());
  }

  update(state: Readonly<CircuitState>): void {
    const gate = GATES[state.gateType];
    this.root.querySelectorAll('[data-gate]').forEach((button) => button.classList.toggle('active', (button as HTMLElement).dataset.gate === state.gateType));
    this.gateTitle.textContent = `${gate.zh} ${gate.en}`;
    this.chipBadge.textContent = gate.chip;
    this.symbol.innerHTML = gateSymbol(state.gateType);
    this.explanation.innerHTML = `<code>${gate.expression}</code><p>${gate.explanation}</p>`;
    this.stateValues.innerHTML = `
      <div><span>A</span><b>${state.inputA}</b><small>${state.switchK1 ? 'K1 闭合接地' : 'K1 断开上拉'}</small></div>
      <div><span>B</span><b>${state.inputB}</b><small>${state.switchK2 ? 'K2 闭合接地' : 'K2 断开上拉'}</small></div>
      <div><span>Y</span><b>${state.outputY}</b><small>${gate.expression}</small></div>`;
    this.ledBadge.className = `led-badge ${state.ledOn ? 'on' : 'off'}`;
    this.ledBadge.innerHTML = `<i></i><span>LED 当前：<b>${state.ledOn ? '亮' : '灭'}</b><small>${state.ledOn ? 'Y=0，输出吸收电流' : 'Y=1，两端压差很小'}</small></span>`;
    this.truthBody.innerHTML = gate.rows.map(({ a, b, y }) => `
      <tr class="${a === state.inputA && b === state.inputB ? 'current' : ''}"><td>${a}</td><td>${b}</td><td>${y}</td><td>${y === 0 ? '亮' : '灭'}</td></tr>`).join('');
    this.root.querySelectorAll<HTMLElement>('[data-switch]').forEach((button) => {
      const k1 = button.dataset.switch === 'K1';
      const closed = k1 ? state.switchK1 : state.switchK2;
      button.classList.toggle('closed', closed);
      button.querySelector('em')!.textContent = closed ? '闭合 · 0' : '断开 · 1';
    });
  }

  showInfo(meta: ComponentMeta): void {
    this.infoCard.querySelector('h2')!.textContent = meta.name;
    this.infoCard.querySelector('h4')!.textContent = `${meta.english} · ${meta.model}`;
    this.infoCard.querySelector('dl')!.innerHTML = `
      <dt>功能</dt><dd>${meta.function}</dd><dt>接线位置</dt><dd>${meta.connection}</dd>
      <dt>常见错误</dt><dd>${meta.mistake}</dd><dt>教学提示</dt><dd>${meta.tip}</dd>`;
    this.infoCard.classList.add('visible');
    this.infoCard.setAttribute('aria-hidden', 'false');
  }

  hideInfo(): void {
    this.infoCard.classList.remove('visible');
    this.infoCard.setAttribute('aria-hidden', 'true');
  }

  setHover(name: string, x: number, y: number): void {
    const el = this.root.querySelector<HTMLElement>('.hover-label')!;
    el.textContent = name;
    el.style.transform = `translate(${x + 14}px, ${y + 14}px)`;
    el.classList.toggle('visible', Boolean(name));
  }

  notify(message: string, kind: 'success' | 'error' | 'info' = 'info'): void {
    this.toast.textContent = message;
    this.toast.className = `toast visible ${kind}`;
    window.setTimeout(() => this.toast.classList.remove('visible'), 2600);
  }

  setDemoCaption(text: string): void {
    this.demoText.textContent = text;
    this.demoText.classList.toggle('visible', Boolean(text));
  }

  showPractice(show: boolean): void {
    this.practicePanel.classList.toggle('visible', show);
    this.practicePanel.setAttribute('aria-hidden', String(!show));
    this.wiringTerminals.classList.toggle('visible', show);
  }

  setPracticeStep(index: number, total: number, text: string, endpoints: string[]): void {
    this.practiceStep.innerHTML = `<b>步骤 ${Math.min(index + 1, total)} / ${total}</b><br>${text}`;
    (this.practicePanel.querySelector('.progress i') as HTMLElement).style.width = `${Math.min(100, index / total * 100)}%`;
    this.wiringTerminals.innerHTML = endpoints.map((id, i) =>
      `<button style="--i:${i}" data-terminal="${id}"><i></i>${id}</button>`).join('');
    this.wiringTerminals.querySelectorAll<HTMLElement>('[data-terminal]').forEach((button) =>
      button.addEventListener('click', () => this.actions.wiringTerminal(button.dataset.terminal!)));
  }

  completePractice(): void {
    this.practiceStep.innerHTML = '<b>✓ 接线完成</b><br>可以拨动 K1、K2 观察逻辑结果。';
    (this.practicePanel.querySelector('.progress i') as HTMLElement).style.width = '100%';
    this.wiringTerminals.innerHTML = '';
  }

  private showGateInfo(): void {
    const active = this.root.querySelector<HTMLElement>('[data-gate].active')?.dataset.gate as GateType;
    const gate = GATES[active];
    this.showInfo({
      id: active, name: gate.zh, english: gate.en, model: gate.chip,
      function: `${gate.explanation} ${gate.expression}`,
      connection: '本实验使用芯片第一组门：1A(1脚)、1B(2脚)、1Y(3脚)。',
      mistake: '把 LED 亮灭直接当作 Y 值；本电路 LED 与 Y 是反相显示。',
      tip: gate.rows.map((r) => `${r.a}${r.b}→${r.y}`).join(' · ')
    });
  }
}
